/**
 * MilkdownEditor - WYSIWYG Markdown 編輯器元件
 *
 * 基於 Milkdown (ProseMirror + remark) 的富文字編輯器
 * 支援真正的所見即所得編輯體驗
 *
 * @module editor/components/MilkdownEditor
 */

import React, { useEffect, useCallback, forwardRef, useImperativeHandle, useRef } from 'react';
import { Editor, rootCtx, defaultValueCtx, editorViewOptionsCtx } from '@milkdown/core';
import { Milkdown, MilkdownProvider, useEditor } from '@milkdown/react';
import { commonmark } from '@milkdown/preset-commonmark';
import { gfm } from '@milkdown/preset-gfm';
import { emoji } from '@milkdown/plugin-emoji';
import { listener, listenerCtx } from '@milkdown/plugin-listener';
import { getMarkdown, replaceAll } from '@milkdown/utils';
import '@milkdown/theme-nord/style.css';

// ============================================================================
// Types
// ============================================================================

export interface MilkdownEditorProps {
  /** 初始 Markdown 內容 */
  initialContent?: string;
  /** 是否為唯讀模式 */
  readonly?: boolean;
  /** 內容變更回調 */
  onChange?: (markdown: string) => void;
  /** 獲得焦點回調 */
  onFocus?: () => void;
  /** 失去焦點回調 */
  onBlur?: () => void;
  /** 自訂 className */
  className?: string;
  /** 自訂樣式 */
  style?: React.CSSProperties;
}

export interface MilkdownEditorRef {
  /** 取得 Markdown 內容 */
  getMarkdown(): string;
  /** 設定 Markdown 內容 */
  setMarkdown(content: string): void;
  /** 聚焦編輯器 */
  focus(): void;
}

// ============================================================================
// Styles
// ============================================================================

const styles = {
  container: {
    width: '100%',
    height: '100%',
    overflow: 'auto',
  },
};

// ============================================================================
// Inner Editor Component
// ============================================================================

interface InnerEditorProps {
  initialContent: string;
  readonly: boolean;
  onChange?: (markdown: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  editorRef: React.MutableRefObject<Editor | null>;
}

const InnerEditor: React.FC<InnerEditorProps> = ({
  initialContent,
  readonly,
  onChange,
  onFocus,
  onBlur,
  editorRef,
}) => {
  // 編輯器初始化
  const { get } = useEditor(
    (root) =>
      Editor.make()
        .config((ctx) => {
          // 設定根元素
          ctx.set(rootCtx, root);
          // 設定初始內容
          ctx.set(defaultValueCtx, initialContent);
          // 設定唯讀模式
          ctx.update(editorViewOptionsCtx, (prev) => ({
            ...prev,
            editable: () => !readonly,
          }));
          // 設定變更監聯器
          // 前端樂觀同步：立即觸發 onChange，無防抖
          // 後端儲存的防抖由 SaveQueueManager (2000ms) 負責
          ctx
            .get(listenerCtx)
            .markdownUpdated((_ctx, markdown, prevMarkdown) => {
              if (markdown !== prevMarkdown && onChange) {
                onChange(markdown);
              }
            })
            .focus(() => {
              onFocus?.();
            })
            .blur(() => {
              onBlur?.();
            });
        })
        .use(commonmark)
        .use(gfm)
        .use(emoji)
        .use(listener),
    [readonly]
  );

  // 儲存編輯器實例
  useEffect(() => {
    const editor = get();
    if (editor) {
      editorRef.current = editor;
    }
  }, [get, editorRef]);

  return <Milkdown />;
};

// ============================================================================
// Main Component
// ============================================================================

/**
 * MilkdownEditor 元件
 *
 * 基於 Milkdown 的 WYSIWYG Markdown 編輯器
 *
 * @example
 * ```tsx
 * const editorRef = useRef<MilkdownEditorRef>(null);
 *
 * <MilkdownEditor
 *   ref={editorRef}
 *   initialContent="# Hello World"
 *   onChange={(md) => console.log(md)}
 * />
 *
 * // 透過 ref 操作
 * editorRef.current?.getMarkdown();
 * editorRef.current?.focus();
 * ```
 */
export const MilkdownEditor = forwardRef<MilkdownEditorRef, MilkdownEditorProps>(
  function MilkdownEditor(props, ref) {
    const {
      initialContent = '',
      readonly = false,
      onChange,
      onFocus,
      onBlur,
      className,
      style,
    } = props;

    const editorRef = useRef<Editor | null>(null);

    // Ref API
    useImperativeHandle(
      ref,
      () => ({
        getMarkdown(): string {
          const editor = editorRef.current;
          if (!editor) return '';
          try {
            return editor.action(getMarkdown());
          } catch {
            return '';
          }
        },

        setMarkdown(content: string): void {
          const editor = editorRef.current;
          if (!editor) return;
          try {
            editor.action(replaceAll(content));
          } catch {
            // 忽略錯誤
          }
        },

        focus(): void {
          const editor = editorRef.current;
          if (!editor) return;
          try {
            const view = editor.ctx?.get(editorViewOptionsCtx);
            // 嘗試聚焦編輯器視圖
            if (view) {
              // Milkdown 的聚焦方式
              const editorDom = document.querySelector('.milkdown .editor');
              if (editorDom instanceof HTMLElement) {
                editorDom.focus();
              }
            }
          } catch {
            // 忽略錯誤
          }
        },
      }),
      []
    );

    // 處理內容變更
    const handleChange = useCallback(
      (markdown: string) => {
        onChange?.(markdown);
      },
      [onChange]
    );

    return (
      <div
        className={className}
        style={{ ...styles.container, ...style }}
        data-testid="milkdown-editor"
      >
        <MilkdownProvider>
          <InnerEditor
            initialContent={initialContent}
            readonly={readonly}
            onChange={handleChange}
            onFocus={onFocus}
            onBlur={onBlur}
            editorRef={editorRef}
          />
        </MilkdownProvider>
      </div>
    );
  }
);

// ============================================================================
// Milkdown Theme Styles (injected once)
// ============================================================================

if (typeof document !== 'undefined') {
  const styleId = 'milkdown-editor-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      /* Milkdown 編輯器基礎樣式 */
      .milkdown {
        width: 100%;
        height: 100%;
        min-height: 100%;
      }

      .milkdown .editor {
        outline: none;
        padding: 16px;
        min-height: 100%;
      }

      /* 標題樣式 */
      .milkdown h1 {
        font-size: 2em;
        font-weight: 700;
        margin-top: 0.67em;
        margin-bottom: 0.67em;
        line-height: 1.2;
      }

      .milkdown h2 {
        font-size: 1.5em;
        font-weight: 700;
        margin-top: 0.83em;
        margin-bottom: 0.83em;
        line-height: 1.3;
      }

      .milkdown h3 {
        font-size: 1.17em;
        font-weight: 700;
        margin-top: 1em;
        margin-bottom: 1em;
        line-height: 1.4;
      }

      /* 段落樣式 */
      .milkdown p {
        margin-top: 1em;
        margin-bottom: 1em;
        line-height: 1.6;
      }

      /* 引用樣式 */
      .milkdown blockquote {
        border-left: 4px solid #e5e7eb;
        padding-left: 16px;
        margin-left: 0;
        margin-right: 0;
        color: #6b7280;
        font-style: italic;
      }

      /* 程式碼區塊 */
      .milkdown pre {
        background-color: #f3f4f6;
        padding: 12px 16px;
        border-radius: 6px;
        font-family: ui-monospace, monospace;
        font-size: 0.875em;
        overflow: auto;
      }

      .milkdown code {
        background-color: #f3f4f6;
        padding: 2px 6px;
        border-radius: 4px;
        font-family: ui-monospace, monospace;
        font-size: 0.875em;
      }

      .milkdown pre code {
        background-color: transparent;
        padding: 0;
      }

      /* 列表樣式 */
      .milkdown ul,
      .milkdown ol {
        padding-left: 24px;
        margin-top: 1em;
        margin-bottom: 1em;
      }

      .milkdown li {
        margin-top: 0.25em;
        margin-bottom: 0.25em;
      }

      /* 連結樣式 */
      .milkdown a {
        color: #2563eb;
        text-decoration: underline;
      }

      .milkdown a:hover {
        color: #1d4ed8;
      }

      /* 分隔線 */
      .milkdown hr {
        border: none;
        border-top: 1px solid #e5e7eb;
        margin-top: 2em;
        margin-bottom: 2em;
      }

      /* 圖片 */
      .milkdown img {
        max-width: 100%;
        height: auto;
      }

      /* 任務列表 (GFM) */
      .milkdown .task-list-item {
        list-style: none;
        margin-left: -24px;
      }

      .milkdown .task-list-item input[type="checkbox"] {
        margin-right: 8px;
      }

      /* 表格 (GFM) */
      .milkdown table {
        border-collapse: collapse;
        width: 100%;
        margin-top: 1em;
        margin-bottom: 1em;
      }

      .milkdown th,
      .milkdown td {
        border: 1px solid #e5e7eb;
        padding: 8px 12px;
        text-align: left;
      }

      .milkdown th {
        background-color: #f9fafb;
        font-weight: 600;
      }

      /* 刪除線 (GFM) */
      .milkdown del {
        text-decoration: line-through;
        color: #6b7280;
      }

      /* High Contrast Mode */
      @media (prefers-contrast: more) {
        .milkdown {
          color: #000 !important;
          background-color: #fff !important;
        }

        .milkdown a {
          color: #00008b !important;
        }

        .milkdown code,
        .milkdown pre {
          background-color: #f0f0f0 !important;
          border: 1px solid #000 !important;
        }

        .milkdown blockquote {
          border-left-color: #000 !important;
          color: #000 !important;
        }

        .milkdown hr {
          border-top-color: #000 !important;
        }

        .milkdown th,
        .milkdown td {
          border-color: #000 !important;
        }
      }

      /* Reduced Motion */
      @media (prefers-reduced-motion: reduce) {
        .milkdown *,
        .milkdown *::before,
        .milkdown *::after {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
        }
      }

      /* Focus styles */
      .milkdown .editor:focus-visible {
        outline: none;
      }

      .milkdown a:focus-visible {
        outline: 2px solid #2563eb;
        outline-offset: 2px;
      }

      @media (prefers-contrast: more) {
        .milkdown a:focus-visible {
          outline: 3px solid #000;
        }
      }

      /* Emoji Plugin Styles - Milkdown Twemoji */
      .milkdown span[data-type="emoji"] {
        display: inline-block;
        vertical-align: middle;
      }

      .milkdown span[data-type="emoji"] img {
        width: 1.2em;
        height: 1.2em;
        vertical-align: -0.2em;
        display: inline-block;
        margin: 0 0.05em;
      }

      .milkdown .emoji {
        width: 1.2em;
        height: 1.2em;
        vertical-align: -0.2em;
        display: inline-block;
        margin: 0 0.05em;
      }
    `;
    document.head.appendChild(style);
  }
}

export default MilkdownEditor;
